import { FEATURE_FLAGS } from '@constants/feature';
import UserPermissionTypes from '@constants/permissions';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import { useAppSelector } from '@core/store';
import {
  useCreateBranchTagMutation,
  useGetReportTagsQuery,
  useUpdateBranchTagMutation
} from '@features/member-management/queries/MemberTag';
import { toUiTag, toUiTags } from '@helpers/tags';
import useCheckPermission from '@hooks/checkPermission';
import useNotify from '@hooks/notify';
import { ApiError } from '@types';
import { getLocalStorageItem } from '@utils/storage';
import { Tag } from 'types/lead-management';

import { DEFAULT_MAX_MANUAL_TAGS } from './constants';
import MemberTagField from './MemberTagField';

const ERROR_MESSAGES: Record<string, string> = {
  ERR_TAG_NAME_ALREADY_EXISTS: 'A tag with this name already exists',
  ERR_CUSTOM_TAG_LIMIT_REACHED:
    'Your branch has reached the maximum number of custom tags',
  ERR_TAG_NOT_EDITABLE: "This tag can't be edited",
  ERR_TAG_NOT_FOUND: 'This tag no longer exists. Refresh and try again'
};

interface OrgTagConfig {
  custom_tag_limit?: number | null;
}

interface MemberTagSectionProps {
  selectedTags: Tag[];
  onSave: (tags: Tag[]) => void;
  onTagUpdated?: (oldId: string, updated: Tag) => void;
  onConfirmOpenChange?: (isOpen: boolean) => void;
  disabled?: boolean;
}

const MemberTagSection = ({
  selectedTags,
  onSave,
  onTagUpdated,
  onConfirmOpenChange,
  disabled
}: MemberTagSectionProps) => {
  const orgFeatureFlags = useAppSelector(
    (state) => state.user.orgFeatureFlags as string[]
  );
  const { createErrorAlert } = useNotify();
  const { checkPermission } = useCheckPermission();
  const orgConfig = useAppSelector(
    (state) => state.user.orgConfig as OrgTagConfig | null
  );
  // Creating and editing branch tags needs branch edit; picking existing tags doesn't
  const canManageTags = checkPermission([UserPermissionTypes.BRANCH_EDIT]);
  const isEnabled = orgFeatureFlags?.includes(FEATURE_FLAGS.MEMBER_TAGGING);

  const organizationId = getLocalStorageItem(
    LOCAL_STORAGE_KEYS.ORGANIZATION_ID
  );
  const branchId = getLocalStorageItem(LOCAL_STORAGE_KEYS.BRANCH_ID);

  // Always fetch fresh: assignability can change outside this modal
  const { data } = useGetReportTagsQuery(
    { organizationId, branchIds: [branchId] },
    {
      skip: !isEnabled || !organizationId || !branchId,
      refetchOnMountOrArgChange: true
    }
  );
  const configuredTagLimit = orgConfig?.custom_tag_limit;
  const customTagLimit =
    typeof configuredTagLimit === 'number' &&
    Number.isFinite(configuredTagLimit)
      ? configuredTagLimit
      : DEFAULT_MAX_MANUAL_TAGS;
  const customTagCount =
    data?.filter((tag) => tag.type === 'custom').length ?? 0;
  const hasReachedCustomTagLimit = customTagCount >= customTagLimit;
  const [createTag] = useCreateBranchTagMutation();
  const [updateTag] = useUpdateBranchTagMutation();

  const showTagError = (error: unknown) => {
    const apiError = error as ApiError;
    const errorCode = apiError?.data?.errorCode;
    createErrorAlert(
      (errorCode ? ERROR_MESSAGES[errorCode] : undefined) ||
        apiError?.data?.message ||
        'Something went wrong. Please try again.'
    );
  };

  const handleCreateTag = async (name: string, color: string) => {
    if (hasReachedCustomTagLimit) {
      createErrorAlert(ERROR_MESSAGES.ERR_CUSTOM_TAG_LIMIT_REACHED);
      return undefined;
    }
    const result = await createTag({
      params: { organizationId, branchId },
      body: { name, color }
    });
    if ('data' in result && result.data) return toUiTag(result.data);
    if ('error' in result) showTagError(result.error);
    return undefined;
  };

  const handleUpdateTag = async (tag: Tag) => {
    const result = await updateTag({
      params: { organizationId, branchId, tagId: tag.id },
      body: { name: tag.name, color: tag.color }
    });
    if ('data' in result && result.data) return toUiTag(result.data);
    if ('error' in result) showTagError(result.error);
    return undefined;
  };

  if (!isEnabled) return null;

  const tagOptions = toUiTags(data);

  return (
    <div>
      <div className="mb-4 text-sm font-semibold text-cofeeNeutral10">Tags</div>
      <MemberTagField
        selectedTags={selectedTags}
        tagOptions={tagOptions}
        onSave={onSave}
        onCreateTag={handleCreateTag}
        onUpdateTag={handleUpdateTag}
        onTagUpdated={onTagUpdated}
        canManageTags={canManageTags}
        onConfirmOpenChange={onConfirmOpenChange}
        disabled={disabled}
      />
    </div>
  );
};

export default MemberTagSection;
